import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import FilmCard from "@/components/feature/FilmCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { getFilmById, getRelatedFilms, getCategoryByKey } from "@/lib/films";
import ShareButton from "./components/ShareButton";

const infoRows = (film: NonNullable<ReturnType<typeof getFilmById>>) => [
  { label: "导演", value: film.director, icon: "ri-user-star-line" },
  { label: "主演", value: film.cast.join(" / "), icon: "ri-group-line" },
  { label: "上映时间", value: `${film.year} 年`, icon: "ri-calendar-line" },
  { label: "片长", value: `${film.duration} 分钟`, icon: "ri-time-line" },
  { label: "地区", value: film.region, icon: "ri-map-pin-line" },
  { label: "类型", value: film.genres.join(" / "), icon: "ri-price-tag-3-line" },
];

export default function DetailPage() {
  const { id } = useParams();
  const film = getFilmById(id);

  useDocumentMeta(
    film ? `《${film.title}》剧情介绍与推荐 - 三级片影视大全` : "影片未找到 - 三级片影视大全",
    film
      ? `${film.year} 年${film.region}影片《${film.title}》，由${film.director}执导。${film.synopsis[0].slice(0, 70)}……`
      : "未找到对应的影片资料，请返回分类页浏览其他作品。",
    film ? `${film.title},${film.title}剧情,${film.genres.join(",")},三级片推荐` : "三级片,影片资料"
  );

  if (!film) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center justify-center px-4 pb-24 pt-40 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-film-line text-2xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">没有找到这部影片</h1>
          <p className="mt-3 max-w-md font-body text-sm leading-relaxed text-foreground-600">
            影片链接可能已经变更，或者片单中暂时没有这部作品。
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/category"
              className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 font-label text-sm text-background-50"
            >
              返回分类页
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/search"
              className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 font-label text-sm text-foreground-800"
            >
              搜索其他影片
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedFilms(film, 4);
  const rows = infoRows(film);
  const primaryCategory = getCategoryByKey(film.categories[0]);

  return (
    <SiteLayout>
      <section className="relative w-full overflow-hidden border-b border-background-300 bg-background-100">
        <div className="absolute inset-0">
          <img
            src={film.backdrop ?? film.poster}
            alt={`《${film.title}》氛围背景图`}
            title={`${film.title} 剧照背景`}
            className="h-full w-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/85 via-background-100/90 to-background-100" />
        </div>

        <div className="relative mx-auto w-full max-w-[1440px] px-4 pb-12 pt-24 md:px-8 md:pb-16 md:pt-28 lg:px-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-500">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="transition-colors hover:text-accent-500">
              分类
            </Link>
            <i className="ri-arrow-right-s-line" />
            {primaryCategory && (
              <>
                <Link
                  to={`/category?type=${primaryCategory.key}`}
                  className="transition-colors hover:text-accent-500"
                >
                  {primaryCategory.name}
                </Link>
                <i className="ri-arrow-right-s-line" />
              </>
            )}
            <span className="text-foreground-800">{film.title}</span>
          </nav>

          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr] lg:gap-12">
            <div className="w-full max-w-[280px] sm:max-w-[320px]">
              <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-300">
                <img
                  src={film.poster}
                  alt={`《${film.title}》高清海报`}
                  title={`${film.title} 海报`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="mt-4 flex items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 py-3">
                <span className="font-label text-[11px] uppercase tracking-[0.18em] text-foreground-500">
                  站内热度
                </span>
                <span className="flex items-center gap-1 font-label text-sm text-accent-500">
                  <i className="ri-fire-line" />
                  {film.heat}
                </span>
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                {film.categories.map((key) => {
                  const category = getCategoryByKey(key);
                  if (!category) return null;
                  return (
                    <Link
                      key={key}
                      to={`/category?type=${key}`}
                      className="rounded-full border border-background-300 bg-background-50/60 px-3 py-1 font-label text-[11px] text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500"
                    >
                      {category.name}
                    </Link>
                  );
                })}
              </div>

              <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[42px] md:leading-[1.15]">
                {film.title}
              </h1>
              <p className="mt-2 font-label text-xs uppercase tracking-[0.32em] text-foreground-500">
                {film.enTitle}
              </p>

              <p className="mt-5 max-w-2xl border-l-2 border-accent-500 pl-4 font-body text-base leading-relaxed text-foreground-700">
                {film.tagline}
              </p>

              <div className="mt-7 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {rows.map((row) => (
                  <div key={row.label} className="flex items-start gap-2.5 border-b border-background-300/70 pb-2.5">
                    <i className={`${row.icon} mt-0.5 text-sm text-accent-500`} />
                    <span className="w-16 shrink-0 font-label text-xs text-foreground-500">{row.label}</span>
                    <span className="min-w-0 font-body text-sm leading-relaxed text-foreground-800">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <ShareButton />
                <Link
                  to={primaryCategory ? `/category?type=${primaryCategory.key}` : "/category"}
                  className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 font-label text-sm text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-500"
                >
                  <i className="ri-arrow-left-line" />
                  返回{primaryCategory ? primaryCategory.name : "分类"}
                </Link>
                <span className="flex items-center gap-1.5 font-label text-[11px] text-foreground-500">
                  <i className="ri-information-line" />
                  本站不提供播放与下载
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16 lg:px-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px] lg:gap-14">
          <article>
            <div className="flex items-center gap-3">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">剧情简介</h2>
            </div>
            <div className="mt-6 space-y-5">
              {film.synopsis.map((paragraph) => (
                <p key={paragraph.slice(0, 10)} className="font-body text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-300 bg-background-100 p-5">
              <p className="font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">内容提示</p>
              <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">
                以上剧情、人物与机构均为虚构示例，仅用于影视资料站的展示与阅读体验，不代表任何真实作品或事件。
                本站面向成年用户，不提供影片播放、下载与交易服务。
              </p>
            </div>
          </article>

          <aside className="lg:pt-1">
            <div className="rounded-lg border border-background-300 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">同分类片单</h2>
              <div className="mt-4 flex flex-col gap-2">
                {film.categories.slice(0, 3).map((key) => {
                  const category = getCategoryByKey(key);
                  if (!category) return null;
                  return (
                    <Link
                      key={key}
                      to={`/category?type=${key}`}
                      className="flex items-center justify-between gap-2 rounded-md border border-background-300 px-3 py-2.5 font-label text-xs text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500"
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${category.icon} text-sm`} />
                        {category.name}
                      </span>
                      <i className="ri-arrow-right-s-line" />
                    </Link>
                  );
                })}
              </div>
              <Link
                to="/search"
                className="mt-4 flex h-10 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 font-label text-xs text-foreground-950 transition-colors hover:bg-primary-600"
              >
                <i className="ri-search-line" />
                搜索更多影片
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full border-t border-background-300 bg-background-100">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16 lg:px-12">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-label text-[11px] uppercase tracking-[0.28em] text-accent-500">You may also like</p>
              <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关影片推荐</h2>
            </div>
            <p className="font-body text-sm text-foreground-600">根据分类与导演线索匹配，共 {related.length} 部。</p>
          </div>
          <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {related.map((item, index) => (
              <FilmCard key={item.id} film={item} index={index} showTagline />
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}